import { useMutation, useQuery, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    InventoryImportCancelJobInput,
    InventoryImportCancelJobResponse,
    InventoryImportCommitInput,
    InventoryImportCommitResponse,
    InventoryImportCreateJobInput,
    InventoryImportCreateJobResponse,
    InventoryImportDraftRow,
    InventoryImportJob,
    InventoryImportListJobsResponse,
    InventoryImportPreviewInput,
    InventoryImportPreviewResponse,
    InventoryImportRowFilter,
    InventoryImportUpdateJobInput,
    InventoryImportUpdateJobResponse,
} from '../../types/inventory';

const createImportJob = async (
    sessionId: string,
    input: InventoryImportCreateJobInput,
): Promise<InventoryImportCreateJobResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/import/jobs/create`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

const listImportJobs = async (
    sessionId: string,
    limit?: number,
): Promise<InventoryImportListJobsResponse> => {
    const { data } = await axios.post(
        `${API_URL}/inventory/import/jobs/list`,
        { limit },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

const previewImportJob = async (
    sessionId: string,
    input: InventoryImportPreviewInput,
): Promise<InventoryImportPreviewResponse> => {
    const { jobId, ...body } = input;
    const { data } = await axios.post(
        `${API_URL}/inventory/import/jobs/${encodeURIComponent(jobId)}`,
        body,
        {
            headers: { Authorization: sessionId },
        },
    );
    return data;
};

const updateImportJob = async (
    sessionId: string,
    input: InventoryImportUpdateJobInput,
): Promise<InventoryImportUpdateJobResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/import/jobs/update`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

/** Direct update for Import-time image staging (avoids Validate Edits mutation side effects). */
export async function updateInventoryImportJobRows(
    sessionId: string,
    input: InventoryImportUpdateJobInput,
): Promise<InventoryImportUpdateJobResponse> {
    return updateImportJob(sessionId, input);
}

/** Poll job list until the job leaves queued/processing (or fails). */
export async function waitForInventoryImportJobSettled(
    sessionId: string,
    jobId: string,
    options?: { pollMs?: number; limit?: number },
): Promise<InventoryImportJob> {
    const pollMs = options?.pollMs ?? 1000;
    const limit = options?.limit ?? 50;
    for (;;) {
        const { jobs } = await listImportJobs(sessionId, limit);
        const job = jobs.find((j) => j.jobId === jobId);
        if (!job) {
            throw new Error('Import job not found');
        }
        if (job.status === 'queued' || job.status === 'processing') {
            await new Promise((resolve) => setTimeout(resolve, pollMs));
            continue;
        }
        if (job.status === 'failed') {
            throw new Error(job.errorMessage || 'Import job failed');
        }
        return job;
    }
}

const cancelImportJob = async (
    sessionId: string,
    input: InventoryImportCancelJobInput,
): Promise<InventoryImportCancelJobResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/import/jobs/cancel`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

const commitImportJob = async (
    sessionId: string,
    input: InventoryImportCommitInput,
): Promise<InventoryImportCommitResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/import/jobs/commit`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

/** Fetch all preview rows for a job (paginated under the hood). */
export async function fetchAllInventoryImportPreviewRows(
    sessionId: string,
    jobId: string,
    rowFilter: InventoryImportRowFilter = 'all',
): Promise<InventoryImportDraftRow[]> {
    const limit = 200;
    let offset = 0;
    const all: InventoryImportDraftRow[] = [];
    for (;;) {
        const page = await previewImportJob(sessionId, { jobId, limit, offset, rowFilter });
        all.push(...page.rows);
        if (all.length >= page.totalItems || page.rows.length === 0) break;
        // Advance by rows actually returned so a lower API cap cannot leave gaps.
        offset += page.rows.length;
    }
    return all;
}

export const useCreateInventoryImportJob = (
    options?: UseMutationOptions<
        InventoryImportCreateJobResponse,
        unknown,
        InventoryImportCreateJobInput
    >,
) => {
    const sessionContext = useContext(SessionContext);
    return useMutation({
        mutationFn: (input: InventoryImportCreateJobInput) =>
            createImportJob(sessionContext?.sessionId ?? '', input),
        ...options,
    });
};

export const useInventoryImportJobsList = (enabled = true, limit = 50) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/inventory/import/jobs/list', sessionContext?.organizationId ?? '', limit],
        queryFn: () => listImportJobs(sessionContext?.sessionId ?? '', limit),
        enabled: enabled && !!sessionContext?.sessionId,
        refetchInterval: (query) => {
            const jobs = query.state.data?.jobs ?? [];
            const busy = jobs.some(
                (j) => j.status === 'queued' || j.status === 'processing',
            );
            return busy ? 2000 : false;
        },
    });
};

export const useInventoryImportPreview = (
    input: InventoryImportPreviewInput | null,
    enabled = true,
) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: [
            '/inventory/import/jobs',
            sessionContext?.organizationId ?? '',
            input?.jobId ?? '',
            input?.limit ?? 20,
            input?.offset ?? 0,
            input?.rowFilter ?? 'all',
        ],
        queryFn: () =>
            previewImportJob(sessionContext?.sessionId ?? '', input as InventoryImportPreviewInput),
        enabled: enabled && !!sessionContext?.sessionId && !!input?.jobId,
    });
};

export const useUpdateInventoryImportJob = (
    options?: UseMutationOptions<
        InventoryImportUpdateJobResponse,
        unknown,
        InventoryImportUpdateJobInput
    >,
) => {
    const sessionContext = useContext(SessionContext);
    return useMutation({
        mutationFn: (input: InventoryImportUpdateJobInput) =>
            updateImportJob(sessionContext?.sessionId ?? '', input),
        ...options,
    });
};

export const useCancelInventoryImportJob = (
    options?: UseMutationOptions<
        InventoryImportCancelJobResponse,
        unknown,
        InventoryImportCancelJobInput
    >,
) => {
    const sessionContext = useContext(SessionContext);
    return useMutation({
        mutationFn: (input: InventoryImportCancelJobInput) =>
            cancelImportJob(sessionContext?.sessionId ?? '', input),
        ...options,
    });
};

export const useCommitInventoryImportJob = (
    options?: UseMutationOptions<
        InventoryImportCommitResponse,
        unknown,
        InventoryImportCommitInput
    >,
) => {
    const sessionContext = useContext(SessionContext);
    return useMutation({
        mutationFn: (input: InventoryImportCommitInput) =>
            commitImportJob(sessionContext?.sessionId ?? '', input),
        ...options,
    });
};
